import React, { useEffect, useRef, useState } from "react";
import {
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { CollisionDetection, DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  IScriptTemplate,
  IScriptTemplateSection,
} from "../types/iscripttemplate";

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type DragHandleListeners = ReturnType<typeof useSortable>["listeners"];
export type DragHandleAttributes = ReturnType<typeof useSortable>["attributes"];

export interface SortableItemProps {
  id: string;
  children: (listeners: DragHandleListeners, attributes: DragHandleAttributes) => React.ReactNode;
}

// ============================================================================
// COMPONENT: SortableItem
// ============================================================================

/**
 * Wraps a section or question in dnd-kit's sortable behavior.
 *
 * The transform/transition/opacity inline style is the one documented
 * exception to the "no inline styles" rule — it's a required per-frame
 * dynamic value dnd-kit computes during drag, matching the existing
 * precedent in form.tsx.
 *
 * `attributes` (role/tabIndex/aria-*) and `listeners` (the pointer/keyboard
 * event handlers dnd-kit's sensors activate from) are both handed to the
 * caller now, rather than `attributes` being spread on this wrapper div —
 * they need to land on the *same* element (NavRailItem's row root, via its
 * `dragHandleProps`) for the KeyboardSensor to work: its `onKeyDown`
 * activator only fires while the element carrying `tabIndex` is actually
 * focused, so splitting them across two different elements (as an earlier
 * version of this file did, with `attributes` here and `listeners` on a
 * separate grip icon) left the row's own focusability and its key handler
 * on different nodes.
 */
export const SortableItem = ({ id, children }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children(listeners, attributes)}
    </div>
  );
};

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Creates a display label with fallback text if the input is empty or whitespace.
 */
export const previewLabel = (text: string, fallback: string): string => {
  const trimmed = text?.trim();
  return trimmed ? trimmed : fallback;
};

/**
 * Detects if the browser prefers reduced motion.
 */
export const prefersReducedMotion = (): boolean => {
  return typeof window !== "undefined"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
};

/**
 * Generates a unique client-side ID for new items.
 */
export const makeClientId = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Returns configured sensors for dnd-kit (pointer and keyboard).
 */
const useDragAndDropSensors = () => {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
};

/**
 * Returns custom collision detection that restricts section drags to section IDs only.
 */
const useDragAndDropCollisionDetection = (): CollisionDetection => {
  return React.useCallback((args) => {
    const activeId = args.active.id as string;
    if (!activeId.startsWith("section:")) {
      return closestCenter(args);
    }
    const sectionContainers = args.droppableContainers.filter((container) =>
      String(container.id).startsWith("section:"),
    );
    return closestCenter({ ...args, droppableContainers: sectionContainers });
  }, []);
};

/**
 * Manages ref tracking and scroll-to functionality for sections and questions.
 */
const useScriptElementTracking = () => {
  const sectionElRefs = useRef(new Map<string, HTMLElement>());
  const questionElRefs = useRef(new Map<string, HTMLDivElement>());

  const registerSectionEl = (clientId: string | undefined) => (el: HTMLElement | null) => {
    if (!clientId) return;
    if (el) sectionElRefs.current.set(clientId, el);
    else sectionElRefs.current.delete(clientId);
  };

  const registerQuestionEl = (clientId: string | undefined) => (el: HTMLDivElement | null) => {
    if (!clientId) return;
    if (el) questionElRefs.current.set(clientId, el);
    else questionElRefs.current.delete(clientId);
  };

  const scrollToSection = (clientId: string | undefined) => {
    if (!clientId) return;
    sectionElRefs.current.get(clientId)?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
  };

  const scrollToQuestion = (clientId: string | undefined) => {
    if (!clientId) return;
    questionElRefs.current.get(clientId)?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center",
    });
  };

  return {
    sectionElRefs,
    questionElRefs,
    registerSectionEl,
    registerQuestionEl,
    scrollToSection,
    scrollToQuestion,
  };
};

/**
 * Manages drag-specific state: isDragging, snapshots, and entering animations.
 */
const useScriptDragState = () => {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartSnapshotRef = useRef<IScriptTemplateSection[] | null>(null);

  const [enteringClientId, setEnteringClientId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!enteringClientId) return;
    const timer = setTimeout(() => setEnteringClientId(undefined), 260);
    return () => clearTimeout(timer);
  }, [enteringClientId]);

  return {
    isDragging,
    setIsDragging,
    dragStartSnapshotRef,
    enteringClientId,
    setEnteringClientId,
  };
};

/**
 * Encapsulates drag event handlers (onDragOver, onDragEnd) for script templates.
 */
interface UseScriptDragHandlersProps {
  values: IScriptTemplate;
  onChange: (values: IScriptTemplate) => void;
}

const useScriptDragHandlers = ({
  values,
  onChange,
}: UseScriptDragHandlersProps) => {
  // Locates a question by clientId by scanning every section
  const findQuestionLocation = (
    clientId: string,
  ): { sectionIndex: number; questionIndex: number } | null => {
    for (let sectionIndex = 0; sectionIndex < values.scriptTemplateSections.length; sectionIndex++) {
      const questionIndex = values.scriptTemplateSections[
        sectionIndex
      ].scriptTemplateQuestions.findIndex((q) => q.clientId === clientId);
      if (questionIndex !== -1) return { sectionIndex, questionIndex };
    }
    return null;
  };

  // Live cross-section preview during drag
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    if (!activeId.startsWith("question:")) return;
    const overId = over.id as string;
    if (activeId === overId) return;

    const activeClientId = activeId.slice("question:".length);
    const from = findQuestionLocation(activeClientId);
    if (!from) return;

    let toSectionIndex: number;
    let toQuestionIndex: number;

    if (overId.startsWith("section:")) {
      const overSectionClientId = overId.slice("section:".length);
      toSectionIndex = values.scriptTemplateSections.findIndex(
        (s) => s.clientId === overSectionClientId,
      );
      if (toSectionIndex === -1) return;
      toQuestionIndex = 0;
    } else if (overId.startsWith("question:")) {
      const overClientId = overId.slice("question:".length);
      const to = findQuestionLocation(overClientId);
      if (!to) return;
      toSectionIndex = to.sectionIndex;
      toQuestionIndex = to.questionIndex;
    } else {
      return;
    }

    if (from.sectionIndex === toSectionIndex) return;

    const sourceSection = values.scriptTemplateSections[from.sectionIndex];
    const destSection = values.scriptTemplateSections[toSectionIndex];
    const sourceQuestions = [...sourceSection.scriptTemplateQuestions];
    const destQuestions = [...destSection.scriptTemplateQuestions];
    const [moved] = sourceQuestions.splice(from.questionIndex, 1);
    destQuestions.splice(toQuestionIndex, 0, moved);
    const newSections = values.scriptTemplateSections.map((s, i) => {
      if (i === from.sectionIndex) return { ...s, scriptTemplateQuestions: sourceQuestions };
      if (i === toSectionIndex) return { ...s, scriptTemplateQuestions: destQuestions };
      return s;
    });
    onChange({ ...values, scriptTemplateSections: newSections });
  };

  // Finalize drag operation
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId.startsWith("question:") && overId.startsWith("section:")) {
      return;
    }

    if (activeId.startsWith("section:")) {
      const activeClientId = activeId.slice("section:".length);
      const overClientId = overId.slice("section:".length);
      const fromIndex = values.scriptTemplateSections.findIndex(
        (s) => s.clientId === activeClientId,
      );
      const toIndex = values.scriptTemplateSections.findIndex(
        (s) => s.clientId === overClientId,
      );
      if (fromIndex === -1 || toIndex === -1) return;
      const newSections = [...values.scriptTemplateSections];
      const [moved] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, moved);
      onChange({ ...values, scriptTemplateSections: newSections });
      return;
    }

    const activeClientId = activeId.slice("question:".length);
    const overClientId = overId.slice("question:".length);
    const from = findQuestionLocation(activeClientId);
    const to = findQuestionLocation(overClientId);
    if (!from || !to) return;

    if (from.sectionIndex === to.sectionIndex) {
      const section = values.scriptTemplateSections[from.sectionIndex];
      const newQuestions = [...section.scriptTemplateQuestions];
      const [moved] = newQuestions.splice(from.questionIndex, 1);
      newQuestions.splice(to.questionIndex, 0, moved);
      const newSections = values.scriptTemplateSections.map((s, i) =>
        i === from.sectionIndex ? { ...s, scriptTemplateQuestions: newQuestions } : s,
      );
      onChange({ ...values, scriptTemplateSections: newSections });
    } else {
      const sourceSection = values.scriptTemplateSections[from.sectionIndex];
      const destSection = values.scriptTemplateSections[to.sectionIndex];
      const sourceQuestions = [...sourceSection.scriptTemplateQuestions];
      const destQuestions = [...destSection.scriptTemplateQuestions];
      const [moved] = sourceQuestions.splice(from.questionIndex, 1);
      destQuestions.splice(to.questionIndex, 0, moved);
      const newSections = values.scriptTemplateSections.map((s, i) => {
        if (i === from.sectionIndex) return { ...s, scriptTemplateQuestions: sourceQuestions };
        if (i === to.sectionIndex) return { ...s, scriptTemplateQuestions: destQuestions };
        return s;
      });
      onChange({ ...values, scriptTemplateSections: newSections });
    }
  };

  return {
    findQuestionLocation,
    handleDragOver,
    handleDragEnd,
  };
};

// ============================================================================
// MAIN COMPOSITE HOOK
// ============================================================================

/**
 * Orchestrates all drag-and-drop concerns into a single, easy-to-use API.
 * Returns configuration for DndContext, event handlers, and utility methods.
 */
export interface UseDragAndDropLogicProps {
  values: IScriptTemplate;
  onChange: (values: IScriptTemplate) => void;
}

export interface UseDragAndDropLogicReturn {
  // DndContext config
  sensors: ReturnType<typeof useSensors>;
  collisionDetection: CollisionDetection;

  // Drag state
  isDragging: boolean;
  dragStartSnapshotRef: React.MutableRefObject<IScriptTemplateSection[] | null>;

  // Event handlers for DndContext
  onDragStart: () => void;
  onDragOver: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;

  // Element tracking & scrolling
  sectionElRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  questionElRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  registerSectionEl: (clientId: string | undefined) => (el: HTMLElement | null) => void;
  registerQuestionEl: (clientId: string | undefined) => (el: HTMLDivElement | null) => void;
  scrollToSection: (clientId: string | undefined) => void;
  scrollToQuestion: (clientId: string | undefined) => void;

  // State
  enteringClientId: string | undefined;
  setEnteringClientId: (clientId: string | undefined) => void;

  // Utilities
  findQuestionLocation: (clientId: string) => { sectionIndex: number; questionIndex: number } | null;
}

export const useDragAndDropLogic = ({
  values,
  onChange,
}: UseDragAndDropLogicProps): UseDragAndDropLogicReturn => {
  const sensors = useDragAndDropSensors();
  const collisionDetection = useDragAndDropCollisionDetection();
  const tracking = useScriptElementTracking();
  const dragState = useScriptDragState();
  const handlers = useScriptDragHandlers({
    values,
    onChange,
  });

  return {
    // Config for DndContext
    sensors,
    collisionDetection,
    isDragging: dragState.isDragging,
    dragStartSnapshotRef: dragState.dragStartSnapshotRef,

    // Event handlers
    onDragStart: () => {
      dragState.dragStartSnapshotRef.current = values.scriptTemplateSections;
      dragState.setIsDragging(true);
    },
    onDragOver: handlers.handleDragOver,
    onDragEnd: (event: DragEndEvent) => {
      dragState.setIsDragging(false);
      dragState.dragStartSnapshotRef.current = null;
      handlers.handleDragEnd(event);
    },
    onDragCancel: () => {
      dragState.setIsDragging(false);
      if (dragState.dragStartSnapshotRef.current) {
        onChange({
          ...values,
          scriptTemplateSections: dragState.dragStartSnapshotRef.current,
        });
      }
      dragState.dragStartSnapshotRef.current = null;
    },

    // Element tracking & scrolling
    ...tracking,

    // State
    enteringClientId: dragState.enteringClientId,
    setEnteringClientId: dragState.setEnteringClientId,

    // Utilities
    findQuestionLocation: handlers.findQuestionLocation,
  };
};
